/**
 * Environment Discovery
 *
 * Discovers Power Automate environments available to the user.
 * Uses Flow API (https://api.flow.microsoft.com) which we already have configured.
 */

import type { AuthProvider } from "../auth/types.js";
import { displayValue } from "../utils/format.js";
import { RESOURCES } from "../auth/types.js";
import { FLOW_API_BASE, FLOW_API_VERSION } from "../utils/constants.js";

// ============================================================================
// Types
// ============================================================================

export interface DiscoveredEnvironment {
  id: string;
  displayName: string;
  region: string;
  type: "Production" | "Sandbox" | "Default" | "Developer" | "Trial";
  isDefault: boolean;
}

interface EnvironmentApiData {
  value?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

// ============================================================================
// Discovery
// ============================================================================

// Flow API for environments (already have this resource configured)
const ENVIRONMENTS_API =
  `${FLOW_API_BASE}/providers/Microsoft.ProcessSimple/environments?api-version=${FLOW_API_VERSION}`;

/**
 * Discover all Power Automate environments accessible to the user
 */
export async function discoverEnvironments(
  authProvider: AuthProvider
): Promise<DiscoveredEnvironment[]> {
  const token = await authProvider.getTokenForResource(RESOURCES.FLOW_SERVICE);

  const response = await fetch(ENVIRONMENTS_API, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Failed to discover environments: ${response.status} ${text}`);
  }

  const data = await response.json() as EnvironmentApiData | Array<Record<string, unknown>>;

  // Handle both array and object with value property
  const environments = Array.isArray(data) ? data : (data.value || []);

  return environments.map((env: Record<string, unknown>) => {
    const props = (env.properties || env) as Record<string, unknown>;
    return {
      id: displayValue(env.name ?? env.id ?? "").split("/").pop() || "",
      displayName: displayValue(props.displayName ?? props.name ?? env.name ?? "Unknown"),
      region: displayValue(props.azureRegionHint ?? props.azureRegion ?? props.location ?? "unknown"),
      type: mapEnvironmentType(displayValue(props.environmentSku ?? props.type ?? "Production")),
      isDefault: Boolean(props.isDefault),
    };
  });
}

function mapEnvironmentType(
  sku: string
): DiscoveredEnvironment["type"] {
  switch (sku) {
    case "Production":
      return "Production";
    case "Sandbox":
      return "Sandbox";
    case "Default":
      return "Default";
    case "Developer":
      return "Developer";
    case "Trial":
      return "Trial";
    default:
      return "Production";
  }
}

/**
 * Get the default environment
 */
export function getDefaultEnvironment(
  environments: DiscoveredEnvironment[]
): DiscoveredEnvironment | undefined {
  return (
    environments.find((e) => e.isDefault) ??
    environments.find((e) => e.type === "Default") ??
    environments[0]
  );
}
